import { NavLink, Outlet } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useSession, can } from '@/shared/auth/session'

/**
 * The Nominations & Party Tickets shell.
 *
 * ## The tab order is the order the work happens in
 *
 * There is nothing to nominate until a party has members, nothing to vote on until a contest
 * has cleared aspirants, and no ticket until somebody has counted. Members first is not a
 * preference; every tab to its right depends on it.
 *
 * ## No tab ships dimmed
 *
 * Every entry below has a route and a backend. A `pending` flag with no destination has
 * shipped twice in this codebase and both times a finished feature's only signal was a greyed
 * label saying it did not exist. If a tab is here, it works.
 */

interface Tab {
  to: string
  label: string
  hint: string
  permission?: string
  end?: boolean
}

interface Group {
  label: string
  tabs: Tab[]
}

export const NOMINATION_GROUPS: Group[] = [
  {
    label: 'Party',
    tabs: [
      {
        to: '/nominations',
        label: 'Members',
        hint: 'The register — one member, one party, linked to the voter roll by ID',
        permission: 'nomination:read',
        end: true,
      },
    ],
  },
  {
    label: 'Contest',
    tabs: [
      {
        to: '/nominations/contests',
        label: 'Contests',
        hint: 'Set up a primary, freeze its roll, open and close voting',
        permission: 'nomination:read',
      },
      {
        to: '/nominations/aspirants',
        label: 'Aspirants',
        hint: 'Applications, fees, vetting and the ballot draw',
        permission: 'nomination:read',
      },
    ],
  },
  {
    label: 'Outcome',
    tabs: [
      {
        to: '/nominations/results',
        label: 'Results & tickets',
        hint: 'Who won, and the candidacy the ticket created',
        permission: 'nomination:read',
      },
      {
        to: '/nominations/analytics',
        label: 'Analytics',
        hint: 'Turnout, the curve, channel mix and what is worth a second look',
        permission: 'nomination:analyse',
      },
    ],
  },
]

export function NominationsLayout() {
  const authorities = useSession((s) => s.authorities)

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-erp-lg font-semibold">Nominations &amp; Party Tickets</h1>
        <p className="text-erp-xs text-muted-foreground mt-0.5">
          Party primaries: who belongs, who stands, who votes, and who gets the ticket.
        </p>
      </div>

      <nav aria-label="Nominations" className="axis-table-scroll border-b border-border">
        <div className="flex items-end gap-6">
          {NOMINATION_GROUPS.map((group) => {
            const visible = group.tabs.filter((t) => can(authorities, t.permission))
            if (visible.length === 0) return null
            return (
              <div key={group.label} className="pb-0">
                <span className="block text-erp-xs uppercase tracking-wide
                                 text-muted-foreground/70 mb-1">
                  {group.label}
                </span>
                <div className="flex items-center gap-1">
                  {visible.map((tab) => (
                    <NavLink key={tab.to} to={tab.to} end={tab.end} title={tab.hint}
                      className={({ isActive }) => cn(
                        'h-9 px-3 inline-flex items-center text-erp-sm whitespace-nowrap',
                        'border-b-2 -mb-px',
                        isActive
                          ? 'border-chocolate text-chocolate font-medium'
                          : 'border-transparent text-muted-foreground hover:text-foreground')}>
                      {tab.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </nav>

      <Outlet />
    </div>
  )
}
